import { useEffect, useState } from "react";
import { useCurrentOrganization } from "src/contexts/OrganizationContext";
import { apiGet } from "../../utils/api";
import Modal from "./Modal";
import SubmitButton from "../SubmitButton";

type DocumentTypeOption = { txa_id: string; label: string };

type UploadClinicalNoteModalProps = {
  onClose: () => void;
  onSubmit: (content: string, documentType: string) => void;
  submitting: boolean;
};

const UploadClinicalNoteModal = ({ onClose, onSubmit, submitting }: UploadClinicalNoteModalProps) => {
  const organization = useCurrentOrganization();
  const [ clinicalNoteTypeOptions, setClinicalNoteTypeOptions ] = useState<DocumentTypeOption[]>([]);
  const [ selectedType, setSelectedType ] = useState<DocumentTypeOption | null>(null);
  const [ content, setContent ] = useState("");
  const hasContent = content.trim().length > 0;
  const missingType = selectedType === null && hasContent;
  const canSubmit = !submitting && selectedType !== null && hasContent;

  useEffect(() => {
    const fetchClinicalNoteTypeOptions = async () => {
      try {
        const response = await apiGet(`/api/organizations/${organization.id}/document-type-options?category=clinical-note`);
        const data = await response.json();
        if (data.options) {
          setClinicalNoteTypeOptions(data.options);
        }
      } catch (_) {}
    };
    fetchClinicalNoteTypeOptions();
  }, [ organization ]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit(content.trim(), selectedType?.txa_id ?? "");
  };

  return (
    <Modal title="Upload Clinical Note" onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="clinical-note-type" className="block text-sm font-medium text-gray-700 mb-1">Clinical Note Type</label>
          <select
            id="clinical-note-type"
            value={selectedType?.txa_id ?? ""}
            onChange={(e) => {
              const opt = clinicalNoteTypeOptions.find((o) => o.txa_id === e.target.value) ?? null;
              setSelectedType(opt);
            }}
            className={`w-full rounded-md border px-3 py-2 text-sm bg-white focus:ring-1 ${
              selectedType === null
                ? "border-amber-400 focus:border-amber-500 focus:ring-amber-500"
                : "border-gray-300 focus:border-sky-500 focus:ring-sky-500"
            }`}
          >
            <option value="">Select a document type</option>
            {clinicalNoteTypeOptions.map((opt) => (
              <option key={opt.txa_id} value={opt.txa_id}>{opt.label}</option>
            ))}
          </select>
          {missingType && (
            <p className="mt-1 text-xs text-amber-600">Please select a document type before uploading.</p>
          )}
        </div>
        <div className="mb-4">
          <label htmlFor="note-content" className="block text-sm font-medium text-gray-700 mb-1">
            Clinical Note Content
          </label>
          <textarea
            id="note-content"
            rows={10}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter clinical note content..."
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500 resize-y"
            autoFocus
          />
        </div>
        <SubmitButton
          loading={submitting}
          text="Upload Note"
          loadingText="Uploading..."
          onClick={handleSubmit}
          disabled={!canSubmit}
        />
      </form>
    </Modal>
  );
};

export default UploadClinicalNoteModal;